<template>
  <div class="min-h-screen bg-gradient-to-bl from-light-cyan via-white to-light-cyan">
    <div
      v-if="loading"
      class="flex h-screen items-center justify-center"
    >
      <LoaderCircle
        :size="100"
        class="animate-spin"
      />
    </div>

    <template v-else>
      <completedQuestionaire
        v-if="submissionIsCompleted"
        :submission-url="submissionUrl"
      />

      <Questionaire
        v-else-if="hasActions && currentSubmission"
        :rawSchema="currentSubmission.schema_ || []"
        :metaData="metadata"
      />

      <client404 v-else />
    </template>
  </div>
</template>

<script setup lang="ts">
  import { ref, computed, onMounted, watch } from 'vue'
  import { useRoute } from 'vue-router'
  import { useClientActionStore } from '@/store/clientActionStore'
  import { createInstructions } from '@/lib/utils'
  import { genericErrorToast } from '@/lib/toasts'
  import { useMetrics, MetricEventType } from '@/composables/useMetrics'
  import Questionaire from '@/components/questionaire/questionaireForm'
  import completedQuestionaire from '@/components/questionaire/completedQuestionaire.vue'
  import { LoaderCircle } from 'lucide-vue-next'
  import client404 from '@/components/404/404.vue'
  import type { ClientPortalQuestionnaireSubmissionSchema } from '@/types'

  const route = useRoute()
  const clientActionsStore = useClientActionStore()
  const { trackEvent } = useMetrics()

  const loading = ref(true)
  const accessToken = computed(() => route.query.access_token as string | '')
  const submissionId = computed(() => route.params.id as string)

  const clientAction = computed(() => clientActionsStore.getClientAction)
  const submissions = computed(() => clientAction.value?.submissions || [])

  const hasActions = computed(() => clientAction.value !== null)
  const submissionIsCompleted = computed(() =>
    submissions.value.some(
      (submission) => submission.shortId === submissionId.value && submission.state === 'CLOSED'
    )
  )
  const submissionUrl = computed(() => {
    const submission = submissions.value.find(
      (submission) => submission.shortId === submissionId.value
    )
    return submission ? submission.pdfUrl : ''
  })

  if (accessToken.value) {
    clientActionsStore.setAccessToken(accessToken.value)
  }

  if (submissionId.value) {
    clientActionsStore.setCurrentSubmissionId(submissionId.value)
  }

  const currentSubmission = computed(() =>
    submissions.value.find((submission) => submission.shortId === submissionId.value)
  )

  const metadata = computed(() => {
    if (!currentSubmission.value) return {}

    return {
      title: currentSubmission.value.title,
      totalQuestionCount: currentSubmission.value.questionCount,
      isPreview: false,
      isExternal: false
    }
  })

  watch([accessToken, submissionId], ([newToken, newId]) => {
    if (newToken) clientActionsStore.setAccessToken(newToken)
    if (newId) clientActionsStore.setCurrentSubmissionId(newId)
  })

  onMounted(async () => {
    try {
      const clientActionStatus = await clientActionsStore.fetchClientActions()
      if (clientActionStatus !== 200) {
        genericErrorToast()
        return
      }

      const orgStatus = await clientActionsStore.fetchOrganisation()
      if (orgStatus !== 200) {
        genericErrorToast()
        return
      }

      if (clientAction.value?.org_id) {
        trackEvent(MetricEventType.CLIENT_QUESTIONNAIRE_EDIT, {
          organization_id: clientAction.value.org_id
        })
      }
    } catch (error) {
      console.error('Error fetching data:', error)
      genericErrorToast()
    } finally {
      loading.value = false
    }
  })
</script>
